import { drawPolygon } from '@civilgrid/ui/utils';
import EventEmitter from 'eventemitter3';
import { filter } from 'lodash';
import type { LngLat as MapboxLngLat, Map as MapboxMap } from 'mapbox-gl';
import type React from 'react';
import { subscribe } from 'valtio';
import { EditorMode, type LineObject, type PolygonObject } from '~/common/types';
import type { MapStoreBase } from '..';
import { mapState } from '../Map';
import { CanvasEvent, type CanvasEvents, DragEvent, HoverEvent } from './CanvasEvent';
import type CanvasObject from './CanvasObject';
import ColorIncrementer from './common/ColorIncrementer';

export interface SurfaceBaseOptions {
  id: string;
  container: HTMLDivElement;
  map: MapboxMap;
  store: MapStoreBase;
}

export interface LngLat {
  lng: number;
  lat: number;
}

export interface Point {
  x: number;
  y: number;
}

interface DragState {
  object: CanvasObject;
  dragStartEmitted: boolean;
  point: Point;
  lngLat: LngLat;
}

export interface ComputedPath {
  path: Path2D;
  center: Point;
  bounds: { width: number; height: number };
  z: number;
  labelData?: {
    labelPoint: Point;
    rotation: number;
    lineLength: number;
  };
}

export interface ToScreenCoordsOptions {
  shouldIncludeBearing?: boolean;
}

export default class SurfaceBase extends EventEmitter<CanvasEvents> {
  id: string;
  canvas: HTMLCanvasElement;
  context: CanvasRenderingContext2D;
  hitCanvas: OffscreenCanvas | HTMLCanvasElement;
  hitCanvasContext: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;
  map: MapboxMap;
  objects: CanvasObject[];
  colorIncrementer: ColorIncrementer;
  hitObjectMap = new Map<string, CanvasObject>();
  // mouse events related
  mouseDownStartTime: number = 0;
  hoveredObject?: CanvasObject;
  dragState?: DragState;

  firstClick: number | null;
  secondClick: number | null;
  isDoubleClicked: boolean;

  store: MapStoreBase;

  currentlySelectedObject?: CanvasObject;
  private mapCenterOnMouseDown?: MapboxLngLat;

  protected pathCache = new WeakMap();

  protected imageCache = new Map();

  protected cleanupFns: Array<() => void>;

  interactionDisabled = false;

  resizeInit: ReturnType<typeof setTimeout>;

  // map bearing in radians for CanvasObject rotation
  mapBearing: number = 0;

  constructor(options: SurfaceBaseOptions) {
    super();
    this.id = options.id;
    this.cleanupFns = [];

    const scale = window.devicePixelRatio;
    this.canvas = document.createElement('canvas');
    const width = options.map.getCanvas().clientWidth;
    const height = options.map.getCanvas().clientHeight;

    this.canvas.width = width * scale;
    this.canvas.height = height * scale;
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.context = this.canvas.getContext('2d')!;
    this.context.scale(scale, scale);
    // A click focuses it so the drawing tools receive keys; it stays out of the tab order because
    // it has no focus indicator and nothing a keyboard-only user can do there yet.
    this.canvas.tabIndex = -1;
    this.canvas.setAttribute('id', 'surface-base');

    //this.hitCanvas = new OffscreenCanvas(width * scale, height * scale);

    this.hitCanvas = document.createElement('canvas');
    this.hitCanvas.width = width * scale;
    this.hitCanvas.height = height * scale;
    this.hitCanvas.style.display = 'none';
    options.container.appendChild(this.hitCanvas);
    this.hitCanvas.style.pointerEvents = 'none';
    // Uncomment to debug hit issues
    this.hitCanvas.style.position = 'absolute';
    this.hitCanvas.style.top = '0px';
    this.hitCanvas.style.left = '0px';

    this.hitCanvasContext = this.hitCanvas.getContext('2d', {
      willReadFrequently: true,
    })!;
    this.colorIncrementer = new ColorIncrementer();

    this.map = options.map;
    this.objects = [];

    this.firstClick = null;
    this.secondClick = null;
    this.isDoubleClicked = false;

    this.store = options.store;

    // Stops the browser from claiming gestures on the overlay as page pan/zoom, so they reach the map.
    this.canvas.style.touchAction = 'none';

    this.canvas.addEventListener('wheel', this.onWheel);
    this.canvas.addEventListener('mousemove', this.onMouseEvent);
    this.canvas.addEventListener('mousedown', this.onMouseEvent);
    this.canvas.addEventListener('mouseup', this.onMouseEvent);
    this.canvas.addEventListener('keydown', this.onKeydown);
    this.canvas.addEventListener('keyup', this.onKeyup);
    this.canvas.addEventListener('touchstart', this.onTouchEvent, { passive: true });
    this.canvas.addEventListener('touchmove', this.onTouchEvent, { passive: false });
    this.canvas.addEventListener('touchend', this.onTouchEvent);
    this.canvas.addEventListener('touchcancel', this.onTouchEvent);

    window.addEventListener('resize', this.resize);

    this.resetCursor();

    this.renderAll();

    options.container.appendChild(this.canvas);

    this.calculateBearing();

    // resize the canvas right away to ensure correct canvas width/height
    this.resizeInit = setTimeout(() => {
      this.resize();
    }, 500);

    // NOTE: "subscribeKey" does not work here to monitor a singular primitive value of the state.
    // Hence why I am monitoring the whole mapState with "subscribe"
    subscribe(mapState, () => this.calculateBearing());
  }

  calculateBearing = () => {
    // If, and only if, this.mapBearing is not equal to the mapState's bearing,
    // then update the mapBearing associated with the SurfaceBase
    if (mapState.viewState.bearing !== this.mapBearing) {
      this.mapBearing = (-1 * (this.map.getBearing() * Math.PI)) / 180;
    }
  };

  destroy() {
    this.canvas.removeEventListener('wheel', this.onWheel);
    this.canvas.removeEventListener('mousemove', this.onMouseEvent);
    this.canvas.removeEventListener('mousedown', this.onMouseEvent);
    this.canvas.removeEventListener('mouseup', this.onMouseEvent);
    this.canvas.removeEventListener('keydown', this.onKeydown);
    this.canvas.removeEventListener('keyup', this.onKeyup);
    this.canvas.removeEventListener('touchstart', this.onTouchEvent);
    this.canvas.removeEventListener('touchmove', this.onTouchEvent);
    this.canvas.removeEventListener('touchend', this.onTouchEvent);
    this.canvas.removeEventListener('touchcancel', this.onTouchEvent);
    this.canvas.remove();

    window.removeEventListener('resize', this.resize);

    this.removeAllObjects();
    this.removeAllListeners();

    clearTimeout(this.resizeInit);

    this.cleanupFns.forEach((fn) => {
      fn();
    });
  }

  getStore() {
    return this.store;
  }

  resize = () => {
    const scale = window.devicePixelRatio;
    const width = this.map.getCanvas().clientWidth;
    const height = this.map.getCanvas().clientHeight;

    this.canvas.width = width * scale;
    this.canvas.height = height * scale;
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.hitCanvas.width = width * scale;
    this.hitCanvas.height = height * scale;
    this.context.scale(scale, scale);
    this.renderAll();
  };

  onWheel = (e: WheelEvent) => {
    e.preventDefault();
    if (this.interactionDisabled) {
      return;
    }
    this.map.getCanvas().dispatchEvent(new WheelEvent('wheel', e));
  };

  onTouchEvent = (e: TouchEvent) => {
    if (this.interactionDisabled) {
      return;
    }
    // Only the move is prevented; the browser's compatibility mouse events after a tap still have to
    // fire so onMouseEvent can turn them into the click mapbox needs to select a feature.
    if (e.type === 'touchmove') {
      e.preventDefault();
    }
    this.bubbleTouchEventToMap(e);
  };

  onMouseEvent = (e: MouseEvent) => {
    if (this.interactionDisabled) {
      return;
    }
    const point = this.screenCoordsFromEvent(e);
    const lngLat = this.toMapCoords(point);
    const target = this.getEventTargetAt(point);

    if (target) {
      if (e.type === 'mousedown') {
        this.mouseDownStartTime = Date.now();
      } else if (e.type === 'mouseup') {
        this.canvas.style.cursor = 'pointer';
        if (this.dragState?.dragStartEmitted) {
          this.dragState.object.emit(
            'dragEnd',
            new DragEvent(
              'dragEnd',
              new DragEvent('dragEnd', {
                startPoint: this.dragState.point,
                startLngLat: this.dragState.lngLat,
                currentPoint: point,
                currentLngLat: lngLat,
              }),
            ),
          );
          this.dragState = undefined;
        } else {
          if (target.id === this.store.currentObjectId) {
            // object is already selected, just pass the click back to the map to handle identify
            // Mapbox needs a mousedown and up, and a click to do the right thing 🤦‍♂️
            // While a tool is active the click belongs to the tool, so mapbox's layer click
            // handlers never see it.
            if (this.store.editorMode === EditorMode.None) {
              this.bubbleEventToMap(new MouseEvent('mousedown', e));
              this.bubbleEventToMap(new MouseEvent('mouseup', e));
              this.bubbleEventToMap(new MouseEvent('click', e));
            }
            // send the "re-tap" too, EditTextObject needs it
            target.emit('tap', new CanvasEvent('tap', { point, lngLat }));
          } else {
            if (Date.now() - this.mouseDownStartTime < 150) {
              // if <150ms, its a regular click
              target.emit('tap', new CanvasEvent('tap', { point, lngLat }));
            }
          }
        }
      } else if (e.type === 'mousemove') {
        if (e.buttons === 1) {
          if (!this.dragState && this.store.currentObjectId) {
            // Setup dragState here in case the actual drag happens outside of the target.
            //That way L307 will still be able to trigger drag events
            this.dragState = {
              object: target,
              point: point,
              lngLat: lngLat,
              dragStartEmitted: false,
            };
          }

          if (Date.now() - this.mouseDownStartTime > 150) {
            // left button is down
            // first time drag
            if (this.dragState) {
              if (!this.dragState.dragStartEmitted) {
                this.dragState.object.emit(
                  'dragStart',
                  new DragEvent('dragStart', {
                    startPoint: point,
                    startLngLat: lngLat,
                    currentPoint: point,
                    currentLngLat: lngLat,
                  }),
                );
                this.dragState.dragStartEmitted = true;
              } else {
                // continue dragging
                this.dragState.object.emit(
                  'dragging',
                  new DragEvent('dragging', {
                    startPoint: this.dragState.point,
                    startLngLat: this.dragState.lngLat,
                    currentPoint: point,
                    currentLngLat: lngLat,
                  }),
                );
              }
              return;
            } else {
              // no drag state, so simulate a map click, this will trigger a full map move event
              this.canvas.style.cursor = 'grabbing';
              this.bubbleEventToMap(new MouseEvent('mousedown', e));
            }
          } else {
            // do nothing, this is probably a tap
          }
        }

        if (this.hoveredObject) {
          // hovered as not changed
          if (this.hoveredObject === target) {
            target.emit('hovering', new HoverEvent('hovering', { point, lngLat }));
          } else {
            // new object is being hovered on
            this.hoveredObject.emit('hoverEnd', new HoverEvent('hoverEnd', { point, lngLat }));
            this.hoveredObject = target;
            target.emit('hoverStart', new HoverEvent('hoverStart', { point, lngLat }));
          }
        } else {
          this.hoveredObject = target;
          this.hoveredObject.emit('hoverStart', new HoverEvent('hoverStart', { point, lngLat }));
        }
      }
    } else {
      // events are happening on the canvas directly
      if (this.hoveredObject) {
        this.hoveredObject.emit('hoverEnd', new HoverEvent('hoverEnd', { point, lngLat }));
        this.hoveredObject = undefined;
      }

      if (e.type === 'mousedown') {
        this.canvas.style.cursor = 'grabbing';
        this.mouseDownStartTime = Date.now();
        if (this.firstClick) {
          this.secondClick = Date.now();

          // Windows OS default dlbTap value is 500ms (we can change this if needed)
          if (this.secondClick - this.firstClick <= 500) {
            this.isDoubleClicked = true;
          }
        }

        this.mapCenterOnMouseDown = this.map.getCenter();
        this.bubbleEventToMap(e);
      }

      if (e.type === 'mouseup') {
        this.canvas.style.cursor = 'default';
        this.firstClick = Date.now();
        // first click expires after 100ms
        this.expireDoubleClick();

        if (this.dragState?.object && this.dragState.dragStartEmitted) {
          this.dragState.object.emit(
            'dragEnd',
            new DragEvent(
              'dragEnd',
              new DragEvent('dragEnd', {
                startPoint: this.dragState.point,
                startLngLat: this.dragState.lngLat,
                currentPoint: point,
                currentLngLat: lngLat,
              }),
            ),
          );
          this.dragState = undefined;
          return;
        }
        // checks to see if double click is true after the most recent mousedown event
        if (this.isDoubleClicked) {
          this.emit('dblTap', new CanvasEvent('dblTap', { point, lngLat }));
          this.bubbleEventToMap(new MouseEvent('dbClick', e));
          this.isDoubleClicked = false;
          return;
        }
        // this may need to be an else if statement, we don't want to emit a 'tap' event if a 'dblTap' was just emitted.
        if (
          this.mapCenterOnMouseDown &&
          this.map.getCenter().distanceTo(this.mapCenterOnMouseDown) < 0.15 // in meters
        ) {
          // Read before emitting: the Edit* tap listeners reset editorMode to None synchronously.
          const toolActive = this.store.editorMode !== EditorMode.None;
          const _e = new CanvasEvent('tap', { point, lngLat });
          this.emit('tap', _e);
          // A tap while a tool is active belongs to the tool, so mapbox's layer click handlers
          // never see it.
          if (_e.shouldBubble() && !toolActive) {
            this.bubbleEventToMap(new MouseEvent('click', e));
          }
        } else {
          this.bubbleEventToMap(e);
        }
      }

      if (e.type === 'mousemove') {
        // item is still being dragged
        if (e.buttons === 1 && this.dragState && Date.now() - this.mouseDownStartTime > 150) {
          if (!this.dragState.dragStartEmitted) {
            this.dragState.object.emit(
              'dragStart',
              new DragEvent('dragStart', {
                startPoint: point,
                startLngLat: lngLat,
                currentPoint: point,
                currentLngLat: lngLat,
              }),
            );
            this.dragState.dragStartEmitted = true;
          }
          // continue dragging
          this.dragState.object.emit(
            'dragging',
            new DragEvent('dragging', {
              startPoint: this.dragState.point,
              startLngLat: this.dragState.lngLat,
              currentPoint: point,
              currentLngLat: lngLat,
            }),
          );
          return;
        }
        this.emit('hovering', new HoverEvent('hovering', { point, lngLat }));
        this.bubbleEventToMap(e);
      }
    }
  };

  disableInteractionEvents = () => {
    this.interactionDisabled = true;
    this.dragState = undefined;
    this.hoveredObject = undefined;
  };

  enableInteractionEvents = () => {
    this.interactionDisabled = false;
  };

  expireDoubleClick = () => {
    setTimeout(() => {
      this.firstClick = null;
    }, 100);
  };

  getEventTargetAt = (point: Point) => {
    const ctx = this.hitCanvasContext;
    const pixel = ctx.getImageData(point.x, point.y, 1, 1).data;
    const color = `rgb(${pixel[0]},${pixel[1]},${pixel[2]})`;
    return this.hitObjectMap.get(color);
  };

  bubbleEventToMap(e: MouseEvent) {
    this.map.getCanvas().dispatchEvent(new MouseEvent(e.type, e));
  }

  bubbleTouchEventToMap(e: TouchEvent) {
    const target = this.map.getCanvas();
    // Mapbox's HandlerManager drops any Touch whose target sits outside its own canvas container, so
    // each Touch is rebuilt against the map canvas rather than forwarded as-is.
    const retarget = (touch: Touch) =>
      new Touch({
        identifier: touch.identifier,
        target,
        clientX: touch.clientX,
        clientY: touch.clientY,
        screenX: touch.screenX,
        screenY: touch.screenY,
        pageX: touch.pageX,
        pageY: touch.pageY,
        radiusX: touch.radiusX,
        radiusY: touch.radiusY,
        rotationAngle: touch.rotationAngle,
        force: touch.force,
      });

    // Only contacts that started on the overlay, so a finger resting elsewhere on the page does not
    // turn a one-finger pan into a two-finger gesture.
    const touches = Array.from(e.targetTouches, retarget);
    const changedTouches = Array.from(e.changedTouches, retarget);

    target.dispatchEvent(
      new TouchEvent(e.type, {
        touches,
        targetTouches: touches,
        changedTouches,
        bubbles: true,
        cancelable: true,
        altKey: e.altKey,
        ctrlKey: e.ctrlKey,
        metaKey: e.metaKey,
        shiftKey: e.shiftKey,
      }),
    );
  }

  // TODO: support scale
  screenCoordsFromEvent = (event: MouseEvent, scale = 1): Point => {
    const rect = this.canvas.getBoundingClientRect();
    const { clientX, clientY } = event;
    const x = (clientX - rect.x) / scale;
    const y = (clientY - rect.y) / scale;
    return { x, y };
  };

  toMapCoords = (point: Point): LngLat => {
    const { lng, lat } = this.map.unproject([point.x, point.y]);
    return { lng, lat };
  };

  toScreenCoords = (lngLat: LngLat, options?: ToScreenCoordsOptions): Point => {
    // this is most likely to happen when the pointer is outside the screen bounds
    if (Number.isNaN(lngLat.lng) || Number.isNaN(lngLat.lat)) {
      return { x: 0, y: 0 };
    }
    const { x, y } = this.map.project(lngLat);

    if (options?.shouldIncludeBearing === false) {
      // const { width, height } = this.map.getCanvas();
      const prevBearing = this.map.getBearing();
      // its a HUUUUGE perf hit to `setBearing`, so setting only if its not 0
      if (prevBearing === 0) {
        return { x, y };
      }
      this.map.setBearing(0);

      const adjustedCoords = this.map.project(lngLat);

      this.map.setBearing(prevBearing);

      return adjustedCoords;
    }

    return { x, y };
  };

  setCursor = (cursor: React.CSSProperties['cursor'] | string) => {
    this.canvas.style.cursor = cursor!;
  };

  resetCursor = () => {
    this.canvas.style.cursor = 'default';
  };

  addObject = (object: CanvasObject) => {
    this.addObjectWithoutRender(object);
    this.renderAll();
  };

  addObjectWithoutRender = (object: CanvasObject) => {
    this.objects.push(object);
  };

  removeObject = (object: CanvasObject) => {
    this.objects = filter(this.objects, (o) => o.id !== object.id);
    this.renderAll();
  };

  removeAllObjects = () => {
    this.objects.forEach((o) => {
      o.destroy();
    });
    this.objects = [];
    this.renderAll();
  };

  // when map viewstate is updated, screenCoords should be updated everywhere
  refreshScreenCoords = () => {
    this.objects.forEach((o) => {
      o.refreshScreenCoords();
    });
    this.renderAll();
  };

  clearCanvas = (ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D) => {
    ctx.save();
    ctx.resetTransform();
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.restore();
  };

  renderWithChildren(objects: CanvasObject[]) {
    objects.forEach((o) => {
      if (o.visible) {
        // render parent first, then children
        o.render(this.context);
        if (o.interactive) {
          const hitColor = this.colorIncrementer.next();
          o.drawHitArea(this.hitCanvasContext, hitColor);
          this.hitObjectMap.set(hitColor, o);
        }
        // render children only if parent is visible
        o.hasChildren() && this.renderWithChildren(o.getChildren());
      }
    });
  }

  sortObjects(objects: CanvasObject[]) {
    this.objects = objects.sort((a, b) => a.sortOrder.localeCompare(b.sortOrder));
  }

  renderAll = () => {
    this.clearCanvas(this.context);
    this.clearCanvas(this.hitCanvasContext);
    this.colorIncrementer.reset();
    this.sortObjects(this.objects);
    this.renderWithChildren(this.objects);
  };

  addSibling = (el: HTMLElement) => {
    this.canvas.parentElement?.appendChild(el);
  };

  private calcCenter(points: Point[]): Point {
    let a = 0,
      x = 0,
      y = 0,
      l = points.length;

    for (let i = 0; i < l; i++) {
      const s = i === l - 1 ? 0 : i + 1,
        v0 = points[i],
        v1 = points[s],
        f = v0.x * v1.y - v1.x * v0.y;

      a += f;
      x += (v0.x + v1.x) * f;
      y += (v0.y + v1.y) * f;
    }

    const d = a * 3;

    return { x: x / d, y: y / d };
  }

  private getBounds(points: Point[]) {
    let minX = points[0].x;
    let maxX = points[0].x;
    let minY = points[0].y;
    let maxY = points[0].y;

    for (let i = 0; i < points.length; i++) {
      minX = Math.min(minX, points[i].x);
      maxX = Math.max(maxX, points[i].x);
      minY = Math.min(minY, points[i].y);
      maxY = Math.max(maxY, points[i].y);
    }

    const width = maxX - minX;
    const height = maxY - minY;

    return { width, height };
  }

  private getLabelLocationData(points: Point[]) {
    let p1 = points[0];
    let p2 = points[1];
    let maxLength = 0;

    for (let i = 0; i < points.length - 1; i++) {
      const xDiff = points[i + 1].x - points[i].x;
      const yDiff = points[i + 1].y - points[i].y;
      const sqrt = Math.sqrt(xDiff * xDiff + yDiff * yDiff);
      if (sqrt > maxLength) {
        p1 = points[i];
        p2 = points[i + 1];
        maxLength = sqrt;
      }
    }

    const lineLength = maxLength;
    const labelPoint = {
      x: p1.x + (p2.x - p1.x) / 2,
      y: p1.y + (p2.y - p1.y) / 2,
    };

    const rawRotation = Math.atan2(p2.y - p1.y, p2.x - p1.x);
    let rotation: number;
    if (rawRotation >= Math.PI / 2 && rawRotation <= Math.PI) {
      rotation = -1 * Math.PI + rawRotation;
    } else if (rawRotation > -1 * Math.PI && rawRotation < (-1 * Math.PI) / 2) {
      rotation = Math.PI + rawRotation;
    } else {
      rotation = rawRotation;
    }
    return { labelPoint, rotation, lineLength };
  }

  getPath = (coordinates: PolygonObject['geometry']['coordinates']): ComputedPath => {
    if (this.pathCache.has(coordinates)) {
      return this.pathCache.get(coordinates);
    } else {
      const rawPolyPoints = coordinates.map((polygon) =>
        polygon.map(([lng, lat]) => this.toScreenCoords({ lng, lat }, { shouldIncludeBearing: false })),
      );

      const polyPoints: Point[][] = rawPolyPoints.map((poly) =>
        poly.map((point) => {
          return {
            x: point.x - rawPolyPoints[0][0].x,
            y: point.y - rawPolyPoints[0][0].y,
          };
        }),
      );

      const path = new Path2D();
      drawPolygon(polyPoints, path);

      const { labelPoint, rotation, lineLength } = this.getLabelLocationData(polyPoints[0]);

      const v = {
        path: path,
        center: this.calcCenter(polyPoints[0]),
        bounds: this.getBounds(polyPoints[0]),
        z: this.map.getZoom(),
        labelData: {
          labelPoint,
          rotation,
          lineLength,
        },
      };

      this.pathCache.set(coordinates, v);
      return v;
    }
  };

  getLinePath = (coordinates: LineObject['geometry']['coordinates']): ComputedPath => {
    if (this.pathCache.has(coordinates)) {
      return this.pathCache.get(coordinates);
    } else {
      const rawPoints = coordinates.map(([lng, lat]) =>
        this.toScreenCoords({ lng, lat }, { shouldIncludeBearing: false }),
      );
      const points: Point[] = [];

      for (let i = 0; i < rawPoints.length; i++) {
        points[i] = {
          x: rawPoints[i].x - rawPoints[0].x,
          y: rawPoints[i].y - rawPoints[0].y,
        };
      }
      const path = new Path2D();
      path.moveTo(points[0].x, points[0].y);
      for (let i = 0; i < points.length; i++) {
        path.lineTo(points[i].x, points[i].y);
      }

      const { labelPoint, rotation, lineLength } = this.getLabelLocationData(points);

      const v = {
        path: path,
        center: this.calcCenter(points),
        bounds: this.getBounds(points),
        z: this.map.getZoom(),
        labelData: {
          labelPoint,
          rotation,
          lineLength,
        },
      };
      this.pathCache.set(coordinates, v);
      return v;
    }
  };

  resetCanvasTransform = (ctx: CanvasRenderingContext2D) => {
    ctx.resetTransform();
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
  };

  onKeydown = (e: KeyboardEvent) => {
    // NOTE: this dispatches any key events on the map to the map itself in Map.tsx
    // Map.tsx has all the keyboard handlers and checks for availability.
    this.map.getCanvas().dispatchEvent(new KeyboardEvent('keydown', e));
  };

  onKeyup = (e: KeyboardEvent) => {
    const { key } = e;
    if (key === 'Enter') {
      this.emit('keyPress', e);
      return;
    }

    if (key === 'Escape') {
      // when editting existing objects
      // NOTE: search string is case sensitive
      if (this.store.editorMode.includes('Edit')) {
        return this.store.resetCurrentObjectId();
      }
      // when creating new objects
      return this.store.setEditorMode(EditorMode.None);
    }
  };
}
